import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getPublicOrder, cancelPublicOrder, PublicOrderError, type PublicOrderResponse } from '../../shared/api/order.js'

type TrackStatus = 'loading' | 'ready' | 'notFound' | 'error'
type CancelState = 'idle' | 'pending' | 'conflict' | 'error'

export default function OrderTrackingPage() {
  const { t } = useTranslation()
  const { orgSlug, publicToken } = useParams<{ orgSlug: string; publicToken: string }>()
  const [status, setStatus] = useState<TrackStatus>('loading')
  const [order, setOrder] = useState<PublicOrderResponse | null>(null)
  const [cancelState, setCancelState] = useState<CancelState>('idle')

  const statusLabel = (raw: string) =>
    t([`orderTracking.status.${raw}`, 'orderTracking.status.unknown'])
  const typeLabel = (raw: string) =>
    t([`orderTracking.type.${raw.toLowerCase()}`, 'storefront.allCategories'])

  function load() {
    if (!publicToken) return
    setStatus('loading')
    getPublicOrder(publicToken)
      .then(data => {
        setOrder(data)
        setStatus('ready')
      })
      .catch(err => {
        if (err instanceof PublicOrderError && err.status === 404) setStatus('notFound')
        else setStatus('error')
      })
  }

  useEffect(() => {
    if (!publicToken) { setStatus('notFound'); return }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicToken])

  async function handleCancel() {
    if (!publicToken || cancelState === 'pending') return
    const ok = window.confirm(t('orderTracking.cancelConfirm'))
    if (!ok) return

    setCancelState('pending')
    try {
      const updated = await cancelPublicOrder(publicToken)
      setOrder(updated)
      setCancelState('idle')
    } catch (err: unknown) {
      if (err instanceof PublicOrderError && err.status === 409) {
        // Another cancellation already resolved this order. Refresh to the latest state.
        setCancelState('conflict')
        load()
      } else {
        setCancelState('error')
      }
    }
  }

  if (status === 'loading') {
    return (
      <div className="storefront-layout">
        <div className="storefront-loading"><div className="spinner" /></div>
      </div>
    )
  }

  if (status === 'notFound' || status === 'error' || !order) {
    return (
      <div className="storefront-layout">
        <div className="storefront-not-found">
          <h2>{status === 'notFound' ? t('orderTracking.notFound') : t('orderTracking.loadError')}</h2>
          <Link className="btn btn-primary" to={`/s/${orgSlug ?? ''}`}>
            {t('orderTracking.backToMenu')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="storefront-layout">
      <header className="storefront-header">
        <div className="storefront-header-brand">
          {order.organization.logoUrl && (
            <img src={order.organization.logoUrl} alt={order.organization.name} className="storefront-logo" />
          )}
          <div>
            <h1 className="storefront-org-name">{order.organization.name}</h1>
          </div>
        </div>
      </header>

      <main className="storefront-main">
        <h2 className="storefront-order-track-title">
          {t('orderTracking.title')} <span className="storefront-order-track-number">{order.orderNumber}</span>
        </h2>

        <div className="storefront-order-track-card">
          <div className="storefront-order-track-row">
            <span>{t('orderTracking.status')}</span>
            <strong className="storefront-order-track-status">{statusLabel(order.status)}</strong>
          </div>
          <div className="storefront-order-track-row">
            <span>{t('orderTracking.type')}</span>
            <strong>{typeLabel(order.type)}</strong>
          </div>
          <div className="storefront-order-track-row">
            <span>{t('orderTracking.createdAt')}</span>
            <strong>{new Date(order.createdAt).toLocaleString()}</strong>
          </div>
          <div className="storefront-order-track-row">
            <span>{t('orderTracking.total')}</span>
            <strong>{order.total} {order.currency}</strong>
          </div>
          {(order.location.name || order.location.address) && (
            <div className="storefront-order-track-row">
              <span>{t('orderTracking.location')}</span>
              <strong>
                {order.location.name}
                {order.location.address && order.location.name && <span>, </span>}
                {order.location.address}
              </strong>
            </div>
          )}
        </div>

        {order.cancellable && (
          <div className="storefront-order-track-cancel">
            <button
              type="button"
              className="btn btn-danger"
              disabled={cancelState === 'pending'}
              onClick={() => void handleCancel()}
            >
              {cancelState === 'pending' ? t('orderTracking.cancelProgress') : t('orderTracking.cancelOrder')}
            </button>
          </div>
        )}
        {!order.cancellable && order.status === 'CANCELLED' && (
          <p className="storefront-order-track-conflict">{t('orderTracking.cancelled')}</p>
        )}
        {cancelState === 'conflict' && (
          <p className="storefront-order-track-conflict">{t('orderTracking.cancelConflict')}</p>
        )}
        {cancelState === 'error' && (
          <p className="storefront-order-track-conflict">{t('orderTracking.cancelError')}</p>
        )}

        {order.statusLog.length > 0 && (
          <>
            <h3 className="storefront-order-track-items-title">{t('orderTracking.timeline')}</h3>
            <ul className="storefront-order-track-timeline">
              {order.statusLog.map((entry, i) => (
                <li key={i} className="storefront-order-track-timeline-item">
                  <span className="storefront-order-track-timeline-status">{statusLabel(entry.status)}</span>
                  <span className="storefront-order-track-timeline-time">{new Date(entry.changedAt).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <h3 className="storefront-order-track-items-title">{t('orderTracking.items')}</h3>
        <ul className="storefront-order-track-items">
          {order.items.map((item, i) => (
            <li key={i} className="storefront-order-track-item">
              <div className="storefront-order-track-item-info">
                <p className="storefront-order-track-item-name">
                  {item.productName}
                  {item.variantName && <span className="storefront-order-track-item-variant">{item.variantName}</span>}
                </p>
                {item.modifiers.length > 0 && (
                  <ul className="storefront-order-track-item-mods">
                    {item.modifiers.map((m, j) => (
                      <li key={j}>{m.name} × {m.quantity}</li>
                    ))}
                  </ul>
                )}
              </div>
              <span className="storefront-order-track-item-price">{item.quantity} × {item.unitPrice} {order.currency}</span>
            </li>
          ))}
        </ul>

        <div className="storefront-order-track-footer">
          <Link className="btn btn-primary" to={`/s/${order.organization.slug}`}>
            {t('orderTracking.backToMenu')}
          </Link>
        </div>
      </main>

      <footer className="storefront-footer">
        <p>{order.organization.name} &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  )
}